/**
 * Central axios client + a `useSiteContent()` hook that fetches all static
 * content in a single request on first mount. Static content rarely changes
 * during a session, so we cache the response in a module-level singleton to
 * avoid duplicate network calls when components mount independently.
 */
import axios from 'axios';
import { useEffect, useState } from 'react';

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Separate axios instance that always sends cookies (for /admin).
export const adminApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.endsWith('/admin/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin/login')) {
        window.location.assign('/admin/login');
      }
    }
    return Promise.reject(error);
  }
);

let cachedContent = null;
let inflight = null;

export function fetchSiteContent() {
  if (cachedContent) return Promise.resolve(cachedContent);
  if (!inflight) {
    inflight = api
      .get('/site/content')
      .then((res) => {
        cachedContent = res.data;
        return cachedContent;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function useSiteContent() {
  const [content, setContent] = useState(cachedContent);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!cachedContent) {
      fetchSiteContent()
        .then((d) => !cancelled && setContent(d))
        .catch((e) => !cancelled && setError(e));
    }
    return () => {
      cancelled = true;
    };
  }, []);

  return { content, error, loading: !content && !error };
}

export async function submitEnquiry(payload) {
  const res = await api.post('/enquiries', payload);
  return res.data;
}
